import { NodeContext, Tree, ITree } from './tree';
import { enumerable } from '../utils/utils';

export interface ITreeNode<T> {
  id: string;
  setParent(node?: ITreeNode<T>): void;
  getParent(): ITreeNode<T>;
  hasParent(): boolean;
  getData(): any;
  addChild(node: ITreeNode<T>): void;
  getChildren(): ITreeNode<T>[];
  getDescendants(): ITreeNode<T>[];
  getAncestors(): ITreeNode<T>[];
  setContext(context?: NodeContext<T>): void;
  belongsTo(tree: NodeContext<T>): boolean;
  isDescendantOf(node: ITreeNode<T>): boolean;
  isAncestorOf(node: ITreeNode<T>): boolean;
}

export class TreeNode<T extends ITreeNode<T>> {
  public id: string;

  private children: ITreeNode<T>[];
  private parent!: ITreeNode<T>;
  private data: any;

  @enumerable(false)
  protected context!: Tree<T>;

  constructor(id: string, data?: any) {
    this.id = id;
    this.data = data;
    this.children = [];
  }

  public setParent(node: ITreeNode<T>) {
    this.parent = node;
  }

  public getParent(): ITreeNode<T> {
    return this.parent;
  }

  public hasParent(): boolean {
    return Boolean(this.parent);
  }

  public addChild(node: ITreeNode<T>) {
    node.setParent(this);
    this.children.push(node);
    if (this.context) {
      this.context.registerNode(node);
    }
  }

  public removeChild(node: ITreeNode<T>) {
    node.setParent(undefined);
    this.children = this.children.filter(candidate => candidate !== node);
    if (this.context) {
      this.context.unregisterNode(node);
    }
  }

  public removeAllChildren() {
    this.children.forEach(child => this.removeChild(child));
  }

  public getChildren(): ITreeNode<T>[] {
    return this.children;
  }

  public hasChildren(): boolean {
    return this.children.length > 0;
  }

  public hasSiblings(): boolean {
    return this.getSiblings().length > 0;
  }

  public getSiblings(): ITreeNode<T>[] {
    if (!this.hasParent()) {
      return [];
    }
    return this.getParent()
      .getChildren()
      .filter(n => n !== this);
  }

  public getFirstChild(): ITreeNode<T> | null {
    if (!this.hasChildren()) {
      return null;
    }
    return this.getChildren()[0];
  }

  public getOrder(): number {
    if (!this.hasParent()) {
      return -1;
    }
    const children = this.getParent().getChildren();
    return children.indexOf(this);
  }

  public isFirstChild(): boolean {
    return this.getOrder() === 0;
  }

  public getDepth(): number {
    let depth = 0;
    let node: ITreeNode<T> = this;
    while (node.hasParent()) {
      node = node.getParent();
      depth++;
    }
    return depth;
  }

  public getData(): any {
    return this.data;
  }

  public getDescendants(): ITreeNode<T>[] {
    let descendants: ITreeNode<T>[] = [];
    let node = this;
    const children = node.getChildren();
    for (const child of children) {
      descendants.push(child);
      const sub = child.getDescendants();
      descendants = descendants.concat(sub);
    }
    return descendants;
  }
  // this currently cannot be easily implemented
  // without copying treenodes (which may be confusing)
  // or getting rid of context/children refs in tree/treenode
  // public getSubtree(): ITree<T> {
  //   const tree = new Tree();
  //   tree.setRootNode(this);
  //   return tree;
  // }

  public getAncestors(): ITreeNode<T>[] {
    let node: ITreeNode<T> = this;
    const ancestors: ITreeNode<T>[] = [];
    while (node.hasParent()) {
      node = node.getParent();
      ancestors.push(node);
    }
    return ancestors;
  }

  public setContext(context: Tree<T>): void {
    this.context = context;
  }

  public belongsTo(tree: Tree<T>): boolean {
    return this.context === tree;
  }

  public isDescendantOf(node: ITreeNode<T>): boolean {
    const descendants = node.getDescendants();
    return descendants.includes(this);
  }

  public isAncestorOf(node: ITreeNode<T>): boolean {
    const ancestors = node.getAncestors();
    return ancestors.includes(this);
  }

  public isRoot(): boolean {
    if (!this.context) return false;
    return this.context.getRootNode() == this;
  }
}
